import React from "react";

import { Link } from "react-router-dom";
import XLSX from "xlsx";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Dropzone from "@/components/Dropzone";
import Modal from "@/components/Modal";

import { useModalStore, useProfileStore } from "@/stores/hooks";

const Title = styled.h4`
  margin: 0;
`;

const Guide = styled.div`
  margin-bottom: 10px;

  a {
    color: #ea2845;
    text-decoration: underline;
  }
`;

const ButtonWrap = styled.div`
  margin-top: 10px;
  text-align: right;
`;

const ETC_NUM_1 = "etcNum1";
const ETC_NUM_2 = "etcNum2";
const ETC_NUM_3 = "etcNum3";
const ETC_NUM_4 = "etcNum4";
const ETC_NUM_5 = "etcNum5";
const PRODUCT_NAME = "상품명";
const BOOK_ID = "예약번호";

export default function ExcelUploadModal({
  title = "대량접수",
  downloadPath = "두발히어로_대량접수샘플",
  onSubmit,
  spot,
  validationType = null,
}) {
  const { user } = useProfileStore().state;

  const { closeModal } = useModalStore();

  const handleUploadBulkFile = async (binary) => {
    const workbook = XLSX.read(binary, {
      type: "binary",
    });

    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const worksheetArr = XLSX.utils.sheet_to_json(worksheet);

    const [headers] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    try {
      if (worksheetArr?.length > 0) {
        const columns = Object.keys(worksheetArr[0]);

        let mustColumns = [];

        if (validationType === "create" && spot && spot.excelSchemeParsed) {
          // 수하인 이름
          if (
            !columns.find((e) => e === spot.excelSchemeParsed?.customerName)
          ) {
            mustColumns.push(spot.excelSchemeParsed.customerName);
          }

          // 수하인 휴대폰 번호
          if (
            !columns.find(
              (e) => e === spot.excelSchemeParsed?.customerMobile,
            ) &&
            !columns.find((e) => e === spot.excelSchemeParsed?.customerMobile2)
          ) {
            mustColumns.push(spot.excelSchemeParsed.customerMobile);
          }

          // 수하인 주소
          if (
            !columns.find(
              (e) => e === spot.excelSchemeParsed?.customerAddress,
            ) &&
            !columns.find(
              (e) => e === spot.excelSchemeParsed?.customerAddressEng,
            )
          ) {
            mustColumns.push(spot.excelSchemeParsed.customerAddress);
          }

          if (mustColumns.length > 0) {
            window.alert(
              `엑셀파일 1행에 필수항목이 누락되었습니다. 누락 항목 수정 후 재접수해주십시오. ${mustColumns
                .map((e) => `[${e}]`)
                .join(" ")}`,
            );
          } else {
            onSubmit(worksheetArr, headers);
          }
        } else if (validationType === "update" && user.isValidateForUpdate) {
          const _worksheetArr = worksheetArr.map((row) => {
            let _row = {
              ...row,
            };

            if (_row[ETC_NUM_1]) {
              _row[ETC_NUM_1] = _row[ETC_NUM_1]?.toString().toUpperCase();
            }
            if (_row[ETC_NUM_2]) {
              _row[ETC_NUM_2] = _row[ETC_NUM_2]?.toString().toUpperCase();
            }
            if (_row[ETC_NUM_3]) {
              _row[ETC_NUM_3] = _row[ETC_NUM_3]?.toString().toUpperCase();
            }
            if (_row[ETC_NUM_4]) {
              _row[ETC_NUM_4] = _row[ETC_NUM_4]?.toString().toUpperCase();
            }
            if (_row[ETC_NUM_5]) {
              _row[ETC_NUM_5] = _row[ETC_NUM_5]?.toString().toUpperCase();
            }

            return _row;
          });

          let unValidValues = [];

          const etcNumValues = [
            ..._worksheetArr.map((e) => e[ETC_NUM_1]),
            ..._worksheetArr.map((e) => e[ETC_NUM_2]),
            ..._worksheetArr.map((e) => e[ETC_NUM_3]),
            ..._worksheetArr.map((e) => e[ETC_NUM_4]),
            ..._worksheetArr.map((e) => e[ETC_NUM_5]),
          ];

          const uniqueEtcNumValues = [...new Set(etcNumValues)];

          if (
            uniqueEtcNumValues.filter((e) => !!e).length !==
            etcNumValues.filter((e) => !!e).length
          ) {
            window.alert(
              "파일 내 같은 유심값이 입력되어 대량수정이 실패했습니다. 중복 여부 확인 후 재접수해주십시오.",
            );

            return;
          }

          if (user.isSk) {
            unValidValues = uniqueEtcNumValues.filter(
              (e) =>
                !!e &&
                (e.length !== 20 || // 20자리가 아닌 경우
                  !/^[a-zA-Z0-9]+$/.test(e) || // 영문, 숫자가 아닌 문자가 포함된 경우
                  e.slice(-1) !== "F"), // 마지막 문자가 F가 아닌 경우
            );

            // 상품명 - etcNum1 코드 매칭 검증 (7~11번째 글자, 5자리)
            const PRODUCT_ETC_NUM1_CODE = {
              간편일반: "25126",
              전용NFC: "21056",
              air전용유심: "25026",
              NFC유심: "21056",
            };

            const mismatchedRows = _worksheetArr.filter((row) => {
              const productName = row[PRODUCT_NAME]?.toString().trim();
              const etcNum1 = row[ETC_NUM_1];

              const matchedProductKey = Object.keys(PRODUCT_ETC_NUM1_CODE).find(
                (key) => productName?.includes(key),
              );

              if (!matchedProductKey) {
                return false;
              }

              if (matchedProductKey && !etcNum1) {
                return true;
              }

              const expectedCode = PRODUCT_ETC_NUM1_CODE[matchedProductKey];
              const actualCode = etcNum1.toString().slice(6, 11) || "";

              return actualCode !== expectedCode;
            });

            if (mismatchedRows.length > 0) {
              const bookIds = mismatchedRows
                .map((row) => row[BOOK_ID])
                .filter((e) => !!e)
                .join(", ");

              window.alert(
                `상품명과 etcNum1 항목값의 조건이 일치하지 않습니다. 유심 확인 후 다시 접수해주십시오.(오류 예약번호: ${bookIds})`,
              );

              return;
            }
          } else if (user.isLgHelloVision) {
            unValidValues = uniqueEtcNumValues.filter(
              (e) =>
                !!e &&
                (e.length !== 19 || // 19자리가 아닌 경우
                  !/^[0-9]+$/.test(e)), // 숫자가 아닌 문자가 포함된 경우
            );
          } else if (user.isZionUsim) {
            _worksheetArr.forEach((row) => {
              const productName = row[PRODUCT_NAME];

              const etcNums = [
                row[ETC_NUM_1],
                row[ETC_NUM_2],
                row[ETC_NUM_3],
                row[ETC_NUM_4],
                row[ETC_NUM_5],
              ];

              if (productName && productName.includes("NFC")) {
                // NFC 상품인 경우: 15자리, 영문+숫자, 첫 문자 C

                etcNums.forEach((etcNum) => {
                  if (
                    !!etcNum &&
                    (etcNum.length !== 15 ||
                      !/^[a-zA-Z0-9]+$/.test(etcNum) ||
                      etcNum.charAt(0) !== "C")
                  ) {
                    unValidValues.push(etcNum);
                  }
                });
              } else if (productName && productName.includes("일반")) {
                // 일반 상품인 경우: 15자리, 영문+숫자, 첫 문자 U

                etcNums.forEach((etcNum) => {
                  if (
                    !!etcNum &&
                    (etcNum.length !== 15 ||
                      !/^[a-zA-Z0-9]+$/.test(etcNum) ||
                      etcNum.charAt(0) !== "U")
                  ) {
                    unValidValues.push(etcNum);
                  }
                });
              }
            });
          }

          if (unValidValues.length > 0) {
            window.alert(
              "입력된 유심값이 해당 기업회원의 기준과 맞지 않아 대량 수정이 실패했습니다. 담당자 확인 후 다시 접수해주십시오.",
            );
          } else {
            onSubmit(_worksheetArr, headers);
          }
        } else {
          onSubmit(worksheetArr, headers);
        }
      }
    } catch (e) {
      // console.log(e)
    }

    closeModal();
  };

  const dropzoneProps = {
    accept: ".xls, .xlsx",
    onUpload: handleUploadBulkFile,
  };

  return (
    <Modal>
      <Modal.Header closeButton={false}>
        <Title>{title}</Title>
      </Modal.Header>
      <Modal.Body>
        <Guide>
          * 주소정보는&nbsp;
          <Link
            target="_blank"
            to={{ pathname: "http://postcode.map.daum.net/guide" }}
          >
            Daum 주소
          </Link>
          를 이용해서 작성해주십시오.
          {downloadPath !== "NONE" && (
            <>
              <br />* {title}용 엑셀파일 다운로드(
              <Link
                target="_blank"
                to={{
                  pathname: `https://doobalhero-files.s3.ap-northeast-2.amazonaws.com/${downloadPath}.xlsx`,
                }}
              >
                여기를 클릭하시면 다운로드 됩니다.
              </Link>
              )
              <br />* 첨부된 엑셀파일에서 접수내용만 수정해서 업로드해주셔야
              정상 등록됩니다.
            </>
          )}
          <br />
          * 주소정보가 맞지 않는 건은 오류 처리되어 등록되지 않습니다. (단건
          등록을 이용해주세요.)
          <br />* {title} 파일 등록 중에 창을 닫는 경우 오류가 날 수 있습니다.
          이용 시 주의 부탁드립니다.
          <br />
        </Guide>
        <Dropzone {...dropzoneProps} />
        <ButtonWrap>
          <Button
            onClick={closeModal}
            type="button"
            variant="outline-secondary"
            width="160px"
          >
            닫기
          </Button>
        </ButtonWrap>
      </Modal.Body>
    </Modal>
  );
}
